/**
 * Global UI State Observable
 * 
 * This store holds all UI state that needs to be shared across components
 * without prop drilling. Uses Legend-State for fine-grained reactivity.
 * 
 * Usage in components:
 *   import { ui$, selectList, openModal } from '../store/ui$';
 *   import { useSelector } from '@legendapp/state/react';
 *   
 *   const sidebarOpen = useSelector(() => ui$.sidebarOpen.get());
 *   // or with useValue:
 *   // import { useValue } from '@legendapp/state/react';
 *   // const sidebarOpen = useValue(ui$.sidebarOpen);
 */

import { observable } from '@legendapp/state';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { syncObservable } from '@legendapp/state/sync';
import { isMobile, isSmallMobile } from '../constants/breakpoints';
import { applyColorTheme } from '../design-system/theme/theme';
import { syncStatusBarWithTheme } from '../lib/native';
export const ui$ = observable({
  // ===== Sidebar =====
  sidebarOpen: false,              // Mobile sidebar overlay toggle (mobile only)
  sidebarCollapsed: true,          // Desktop sidebar starts as an icon rail; pinning expands it persistently

  // ===== List Navigation =====
  currentListId: 'all',            // 'all' | null (inbox) | uuid - for article filtering
  heatmapScope: 'all',             // 'all' | null (inbox) | uuid - for calendar display
  returnPath: '/all',              // Path to return to when leaving article view
  showTranslatedOnly: false,       // Only show articles with translations
  showAudioOnly: false,            // Only show articles with audio
  hideSharedArticles: false,       // Hide articles from shared lists (All view only)
  viewingUserId: null,             // Viewing other user's progress
  sortBy: 'created_at',            // 'created_at' | 'last_viewed_at' | 'rating'
  viewMode: 'normal',              // 'normal' | 'compact' | 'list' | 'card'
  cardSearchKeyword: '',           // Search/filter the open deck's cards by keyword
  selectedTags: [],                // Selected tags for filtering articles
  activeTab: 'articles',           // Current tab: 'articles' | 'archived' | 'activities'

  // ===== Theme =====
  colorTheme: 'light',             // 'light' | 'dark'; legacy values migrate on app startup

  // ===== Reader =====
  aiPanelHidden: false,            // AI panel visibility (desktop + mobile)
  aiPanelWidth: 340,               // AI panel width in pixels (draggable)
  floatingUIHidden: false,         // Mobile scroll-to-hide floating UI (toolbar, feedback)
  focusMode: 'off',                // Focus guide mode: 'off' | 'line' | 'zone'

  // ===== Global RAG Chat =====
  ragPanelHidden: true,            // RAG chat panel visibility (list views)

  // ===== Modals =====
  activeModal: null,               // 'upload' | 'snippet' | 'extension' | 'confirmDelete' | 'invite' | 'members' | 'pdfGate' | null
  modalData: null,                 // Data for modal (e.g., { listId: '...' })

  // ===== Comment Panel (Mobile) =====
  commentPanelOpen: false,         // Mobile slide panel
  commentPanelTarget: null,        // { type, blockIndex?, highlightId? }

  // ===== Search Modal =====
  searchModalOpen: false,          // Cmd+K spotlight search modal

  // ===== Keyboard Shortcuts =====
  shortcutsModalOpen: false,       // Shortcuts cheat sheet modal
  selectedArticleIndex: -1,        // Currently selected article in list (keyboard nav)
  hoveredBlockIndex: -1,           // Currently hovered block in reader (keyboard nav)

  // ===== IME Composition =====
  isComposing: false,              // IME (Input Method Editor) composition state for CJK input

  // ===== Usage/Upgrade Modal =====
  usageModalOpen: false,           // Global trigger for UsageModal
  usageModalTab: 'balance',        // 'balance' | 'plan' | 'history'
  upgradePromptFeature: null,      // Feature that triggered the upgrade prompt (for context)

  // ===== Onboarding =====
  onboardingCompleted: false,      // One-time onboarding overlay dismissed

  // ===== Confirmations =====
  skipDeleteConfirmation: false,   // Skip delete confirmation dialog
  skipReadUntilConfirmation: false, // Skip confirmation when marking skipped paragraphs read

  // ===== Comment Column (Article View) =====
  commentColumnVisible: true,      // Comment gutter visibility in article view
  articleCommentCount: 0,          // Current article's comment count (for header display)

  // ===== Right Panel Tab =====
  activeRightPanelTab: 'chat',     // 'outline' | 'chat' | 'mindmap'

  // ===== AI Panel Actual Visibility =====
  // This is true only when the AI panel is actually visible on screen
  // (i.e., in learn mode AND aiPanelHidden is false)
  // Set by ReaderContent.jsx to coordinate header layout
  isAIPanelActuallyVisible: false,
});

// ===== Persistence boundary =====
//
// `syncObservable` in Legend-State 3.0.0-beta has no partial-persist option: a
// bare `persist: { name }` writes every field of the observable to localStorage
// and hydrates all of them back, which used to revive stale search words, open
// modals, and keyboard-selection state on refresh. The allowlist below is the
// single source of truth for what may reach storage; everything else in `ui$`
// is transient and dies with the page.
//
// Allowlisted: real user preferences and navigation context only.
// Never allowlist: search words (ui$.cardSearchKeyword, panelSearch$ entries),
// modal/panel open flags, keyboard selection, IME state, derived flags.
const UI_PERSISTED_KEYS = new Set([
  // Sidebar & navigation context
  'sidebarCollapsed',
  'currentListId',
  'heatmapScope',
  'returnPath',
  // Library filters that are documented preferences, not transient ones
  'showTranslatedOnly',
  'showAudioOnly',
  'hideSharedArticles',
  // List presentation
  'sortBy',
  'viewMode',
  // Appearance & reader workspace
  'colorTheme',
  'aiPanelHidden',
  'aiPanelWidth',
  'focusMode',
  'commentColumnVisible',
  'activeRightPanelTab',
  // One-time flags
  'onboardingCompleted',
  'skipDeleteConfirmation',
  'skipReadUntilConfirmation',
]);

const isUiPersistTable = (table) => table === 'reedle-ui';

const filterPersistedUi = (data) => {
  if (!data || typeof data !== 'object') return data;
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => UI_PERSISTED_KEYS.has(key))
  );
};

/**
 * The stock plugin stores the whole observable and would happily save/hydrate
 * transient fields. This subclass enforces the allowlist at the plugin
 * boundary: `getTable` drops non-allowlisted keys before they can hydrate (so
 * existing `reedle-ui` records cannot revive them), and `set` ignores changes
 * to them (so they never get written). The plugin's cache is replaced with the
 * filtered object so a later save cannot re-emit stale keys it loaded.
 */
class UIPersistLocalStorage extends ObservablePersistLocalStorage {
  getTable(table, init) {
    const data = super.getTable(table, init);
    if (!isUiPersistTable(table)) return data;
    const filtered = filterPersistedUi(data);
    this.data[table] = filtered;
    return filtered;
  }

  set(table, changes) {
    if (!isUiPersistTable(table)) return super.set(table, changes);
    const allowed = changes.filter((change) => UI_PERSISTED_KEYS.has(change.path?.[0]));
    if (allowed.length === 0) return undefined;
    return super.set(table, allowed);
  }
}

syncObservable(ui$, {
  persist: {
    name: 'reedle-ui',
    plugin: UIPersistLocalStorage,
    retrySync: true,
  },
});

// Mobile guard: Force AI panels closed on mobile to prevent full-screen overlay on launch
// This runs after localStorage hydration to ensure persisted "open" state doesn't show on mobile
if (typeof window !== 'undefined' && isMobile()) {
  ui$.aiPanelHidden.set(true);
  ui$.ragPanelHidden.set(true);
}

// Non-persisted: replay mode resets on page refresh (must NOT be inside syncObservable)
export const onboardingReplay$ = observable(false);

// Reader block lazy-reveal: -1 = all blocks revealed (short articles / default).
// For articles with >REVEAL_THRESHOLD blocks, ReaderContent sets this to INITIAL_REVEAL
// and increments as user scrolls. TrackableBlock renders a lightweight placeholder
// for blocks with index >= revealedCount$ when revealedCount$ >= 0.
// Must be reset to -1 on every article navigation to avoid stale state.
export const revealedCount$ = observable(-1);

// ===== Helper Actions =====

// List navigation
export const selectList = (listId) => {
  ui$.currentListId.set(listId);
  ui$.viewingUserId.set(null); // Reset when changing lists
  ui$.sidebarOpen.set(false);  // Close mobile sidebar
};

export const setShowTranslatedOnly = (show) => {
  ui$.showTranslatedOnly.set(show);
};

export const setShowAudioOnly = (show) => {
  ui$.showAudioOnly.set(show);
};

export const setHideSharedArticles = (hide) => {
  ui$.hideSharedArticles.set(hide);
};

export const setSortBy = (sortBy) => {
  ui$.sortBy.set(sortBy);
};

export const setViewMode = (viewMode) => {
  ui$.viewMode.set(viewMode);
};

export const setViewingUserId = (userId) => {
  ui$.viewingUserId.set(userId);
};

export const resetViewingUser = () => {
  ui$.viewingUserId.set(null);
};

/**
 * The deck's card filter. It lives here rather than inside the card list because
 * two surfaces write it — the list's own search bar and the deck panel's keyword
 * mode — and both have to narrow the same list instead of producing a second,
 * competing set of results. Cleared when a deck is left (DeckView) and never
 * persisted, so a refresh cannot resurrect it into another deck.
 */
export const setCardSearchKeyword = (keyword) => {
  ui$.cardSearchKeyword.set(keyword);
};

// Tag filtering
export const setSelectedTags = (tags) => {
  ui$.selectedTags.set(tags);
};

export const toggleTagFilter = (tag) => {
  const current = ui$.selectedTags.get() || [];
  if (current.includes(tag)) {
    ui$.selectedTags.set(current.filter(t => t !== tag));
  } else {
    ui$.selectedTags.set([...current, tag]);
  }
};

export const clearTagFilters = () => {
  ui$.selectedTags.set([]);
};

export const clearAllFilters = () => {
  ui$.selectedTags.set([]);
};

// Active tab (articles/archived/activities)
export const setActiveTab = (tab) => {
  ui$.activeTab.set(tab);
};

// Theme
export const setColorTheme = (theme) => {
  const resolvedTheme = applyColorTheme(theme);
  ui$.colorTheme.set(resolvedTheme);
  // Native chrome is part of the same appearance switch — the status bar has to
  // repaint with the canvas, not stay on whatever it booted with.
  syncStatusBarWithTheme(resolvedTheme);
  return resolvedTheme;
};

// Sidebar
export const toggleSidebar = () => {
  ui$.sidebarOpen.set(!ui$.sidebarOpen.get());
};

export const setSidebarOpen = (open) => {
  ui$.sidebarOpen.set(open);
};

// Desktop sidebar collapsed toggle (persistent)
export const toggleSidebarCollapsed = () => {
  ui$.sidebarCollapsed.set(!ui$.sidebarCollapsed.get());
};

// Responsive sidebar toggle: mobile uses overlay (sidebarOpen), desktop uses collapse (sidebarCollapsed)
export const toggleSidebarResponsive = () => {
  if (typeof window !== 'undefined' && isSmallMobile()) {
    ui$.sidebarOpen.set(!ui$.sidebarOpen.get());
  } else {
    ui$.sidebarCollapsed.set(!ui$.sidebarCollapsed.get());
  }
};

// Modal management
export const openModal = (type, data = null) => {
  ui$.activeModal.set(type);
  ui$.modalData.set(data);
};

export const closeModal = () => {
  ui$.activeModal.set(null);
  ui$.modalData.set(null);
};

// AI Panel (desktop + mobile)
export const openAIPanel = () => {
  ui$.aiPanelHidden.set(false);
};

export const closeAIPanel = () => {
  ui$.aiPanelHidden.set(true);
};

export const toggleAIPanel = () => {
  ui$.aiPanelHidden.set(!ui$.aiPanelHidden.get());
};

export const AI_PANEL_MIN_WIDTH = 340;
export const AI_PANEL_MAX_WIDTH = 600;

export const setAIPanelWidth = (width) => {
  const clampedWidth = Math.max(AI_PANEL_MIN_WIDTH, Math.min(AI_PANEL_MAX_WIDTH, width));
  ui$.aiPanelWidth.set(clampedWidth);
};

export const getAIPanelWidth = () => {
  return ui$.aiPanelWidth.get() || AI_PANEL_MIN_WIDTH;
};

// Global RAG Panel (list views)
export const openRAGPanel = () => {
  ui$.ragPanelHidden.set(false);
};

export const closeRAGPanel = () => {
  ui$.ragPanelHidden.set(true);
};

export const toggleRAGPanel = () => {
  ui$.ragPanelHidden.set(!ui$.ragPanelHidden.get());
};

// AI Panel actual visibility (set by ReaderContent based on learn mode + panel state)
export const setAIPanelActuallyVisible = (visible) => {
  ui$.isAIPanelActuallyVisible.set(visible);
};

// Comment Panel (mobile)
export const openCommentPanel = (target) => {
  ui$.commentPanelOpen.set(true);
  ui$.commentPanelTarget.set(target);
};

export const closeCommentPanel = () => {
  ui$.commentPanelOpen.set(false);
  ui$.commentPanelTarget.set(null);
};

// Shortcuts Modal
export const openShortcutsModal = () => {
  ui$.shortcutsModalOpen.set(true);
};

export const closeShortcutsModal = () => {
  ui$.shortcutsModalOpen.set(false);
};

export const toggleShortcutsModal = () => {
  ui$.shortcutsModalOpen.set(!ui$.shortcutsModalOpen.get());
};

// Search Modal
export const openSearchModal = () => {
  ui$.searchModalOpen.set(true);
};

export const closeSearchModal = () => {
  ui$.searchModalOpen.set(false);
};

export const toggleSearchModal = () => {
  ui$.searchModalOpen.set(!ui$.searchModalOpen.get());
};

// Article Selection (keyboard navigation)
export const setSelectedArticleIndex = (index) => {
  ui$.selectedArticleIndex.set(index);
};

export const clearSelectedArticle = () => {
  ui$.selectedArticleIndex.set(-1);
};

// Block Hover (keyboard navigation in reader)
export const setHoveredBlockIndex = (index) => {
  ui$.hoveredBlockIndex.set(index);
};

export const clearHoveredBlock = () => {
  ui$.hoveredBlockIndex.set(-1);
};

// IME Composition (for CJK input handling)
export const setIsComposing = (composing) => {
  ui$.isComposing.set(composing);
};

// Delete Confirmation
export const setSkipDeleteConfirmation = (skip) => {
  ui$.skipDeleteConfirmation.set(skip);
};

// Floating UI (mobile scroll-to-hide)
export const setFloatingUIHidden = (hidden) => {
  ui$.floatingUIHidden.set(hidden);
  // Also toggle body class for Featurebase widget CSS targeting
  if (hidden) {
    document.body.classList.add('floating-ui-hidden');
  } else {
    document.body.classList.remove('floating-ui-hidden');
  }
};

// Focus Guide Mode
export const setFocusMode = (mode) => {
  ui$.focusMode.set(mode);
};

export const toggleFocusMode = () => {
  const current = ui$.focusMode.get();
  // Toggle: off -> zone -> off (keyboard shortcut cycles through zone mode)
  if (current === 'off') {
    ui$.focusMode.set('zone');
  } else {
    ui$.focusMode.set('off');
  }
};

// Right Panel Tab
export const setActiveRightPanelTab = (tab) => {
  ui$.activeRightPanelTab.set(tab);
};

// Comment Column (Article View)
export const setCommentColumnVisible = (visible) => {
  ui$.commentColumnVisible.set(visible);
};

export const toggleCommentColumn = () => {
  ui$.commentColumnVisible.set(!ui$.commentColumnVisible.get());
};

export const setArticleCommentCount = (count) => {
  ui$.articleCommentCount.set(count);
};

// Upload Modal (global trigger shared by Sidebar Add and compact header +)
export const openUploadModal = () => {
  openModal('upload');
};

export const closeUploadModal = () => {
  closeModal();
};

// Usage/Upgrade Modal (global access for UpgradePrompt from any component)
// tab: 'balance' — credit depletion triggers (shows balance + upsell banner + top-up buttons)
//      'plan'    — feature-gate triggers (PDF, RAG, advanced models, article limit)
//      'history' — direct link to usage log
export const openUsageModal = (tab = 'balance', feature = null) => {
  ui$.usageModalTab.set(tab);
  ui$.upgradePromptFeature.set(feature);
  ui$.usageModalOpen.set(true);
};

export const closeUsageModal = () => {
  ui$.usageModalOpen.set(false);
  ui$.usageModalTab.set('balance');
  ui$.upgradePromptFeature.set(null);
};
